/**
 * The Zoom Video SDK keeps the camera capture alive after `stopVideo()` so a
 * later `startVideo()` can reuse it — the webcam LED stays on for the whole
 * call even while video is off. v2.4 exposes no API to release it, so we
 * record the camera tracks the SDK acquires and stop the leftovers ourselves
 * after a stop; the SDK re-acquires a fresh track on the next `startVideo()`.
 */

const cameraTracks = new Set<MediaStreamTrack>()
let patched = false

/** Wrap `getUserMedia` to record acquired camera tracks. Call before the SDK's
 *  first acquisition (start of `joinSession`). Idempotent. */
export function watchCameraAcquisition(): void {
  if (patched || typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return
  patched = true
  const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices)
  navigator.mediaDevices.getUserMedia = async (constraints) => {
    const stream = await original(constraints)
    stream.getVideoTracks().forEach((track) => {
      cameraTracks.add(track)
      track.addEventListener('ended', () => cameraTracks.delete(track))
    })
    return stream
  }
}

/** Stop any camera track still live. Only call while video is OFF — a live
 *  track here is the leak, but during video it is the active feed. */
export function releaseCameraCapture(): void {
  for (const track of cameraTracks) {
    if (track.readyState === 'live') track.stop()
    cameraTracks.delete(track)
  }
}
